import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Card, Chip, Container, Divider, IconButton, Menu, MenuItem, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography, Skeleton, LinearProgress,
} from '@mui/material';
import { Icon } from '@iconify/react';
import plusFill from '@iconify/icons-eva/plus-fill';
import flashFill from '@iconify/icons-eva/flash-fill';
import moreVerticalFill from '@iconify/icons-eva/more-vertical-fill';
import searchFill from '@iconify/icons-eva/search-fill';
import pieChart2Fill from '@iconify/icons-eva/pie-chart-2-fill';
import messageSquareFill from '@iconify/icons-eva/message-square-fill';
import Page from '../Page';
import PageHeader from './shared/PageHeader';
import { showErrorToast, showSuccessToast } from '../../utils/Toast/SnackbarProvider';
import StatusChip from './shared/StatusChip';
import KpiCard from './shared/KpiCard';
import ChatCampaignWizard from './ChatCampaignWizard';
import {
  listCampaigns, activateCampaign, pauseCampaign, resumeCampaign, archiveCampaign,
} from '../../utils/outreachApi';

// Compact date — e.g. "23 May, 14:32"
const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
       + ', ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

const STATUS_FILTERS = [
  { key: 'all',       label: 'All' },
  { key: 'draft',     label: 'Draft' },
  { key: 'review',    label: 'Review' },
  { key: 'active',    label: 'Active' },
  { key: 'paused',    label: 'Paused' },
  { key: 'completed', label: 'Completed' },
  { key: 'archived',  label: 'Archived' },
];
const pct = (num, den) => (den ? `${Math.round((num / den) * 100)}%` : '—');

const CampaignList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [menu, setMenu] = useState({ anchor: null, row: null });
  const [chatOpen, setChatOpen] = useState(false);

  const fetchRows = () => listCampaigns().then(({ data }) => setRows(data));
  const load = () => {
    setRows(null);
    fetchRows();
  };
  useEffect(() => { load(); }, []);

  // Keep the list fresh while any campaign is still validating companies, so
  // the per-row progress bar advances on its own. Silent refetch (no skeleton
  // flash) every 10s; stops once nothing is in-flight.
  useEffect(() => {
    if (!rows) return undefined;
    const anyValidating = rows.some((r) => (r.enrichment_progress?.in_flight ?? 0) > 0);
    if (!anyValidating) return undefined;
    const t = setInterval(fetchRows, 10000);
    return () => clearInterval(t);
  }, [rows]);

  const filtered = useMemo(() => {
    if (!rows) return null;
    return rows.filter((r) => {
      const effective = r.display_status || r.status;
      if (statusFilter !== 'all' && effective !== statusFilter) return false;
      if (search && !r.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [rows, search, statusFilter]);

   const summary = useMemo(() => {
     if (!rows) return null;
     return {
       total: rows.length,
       active: rows.filter((r) => r.status === 'active').length,
       in_flight: rows.reduce((s, r) => s + (r.status === 'active' ? r.counts.enrolled_count - r.counts.replied_count : 0), 0),
       replied: rows.reduce((s, r) => s + r.counts.replied_count, 0),
     };
   }, [rows]);

  const onMenuOpen = (e, row) => setMenu({ anchor: e.currentTarget, row });
  const onMenuClose = () => setMenu({ anchor: null, row: null });

  const onAction = async (action) => {
    const row = menu.row;
    onMenuClose();
    if (!row) return;
    if (action === 'edit')     navigate(`/outreach/campaigns/${row.id}/edit`);
    if (action === 'open')     navigate(`/outreach/campaigns/${row.id}`);
    if (action === 'view')     navigate(`/outreach/campaigns/${row.id}/conversations`);
    if (action === 'activate') {
      try {
        await activateCampaign(row.id);
        showSuccessToast('Campaign is activating — refresh in a moment to see it go live.');
        load();
      } catch (err) {
        const msg = err?.response?.data?.detail || 'Activation failed.';
        showErrorToast(msg);
      }
      return;
    }
    if (action === 'pause')    { await pauseCampaign(row.id); load(); }
    if (action === 'resume')   { await resumeCampaign(row.id); load(); }
    if (action === 'archive')  { await archiveCampaign(row.id); load(); }
  };

  return (
    <Page title="Outreach Campaigns | CRM">
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <PageHeader
          title="Outreach Campaigns"
          subtitle="Multi-step sequences that target non-outreached companies and run via BAs' Outlook + LinkedIn"
          actions={
            <Button variant="contained" startIcon={<Icon icon={flashFill} />}
                    onClick={() => navigate('/outreach/campaigns/new')}>
              New Campaign
            </Button>
          }
        />

        {summary ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
            <KpiCard label="Total"       value={summary.total} />
            <KpiCard label="Active"      value={summary.active}    color="success.main" />
            <KpiCard label="In flight"   value={summary.in_flight.toLocaleString()} hint="enrolled, awaiting reply" />
            <KpiCard label="Replied"     value={summary.replied}   color="warning.dark" />
          </Stack>
        ) : (
          <Skeleton variant="rounded" height={96} sx={{ mb: 3 }} />
        )}

        <Card variant="outlined" sx={{ overflow: 'hidden' }}>
          {/* Filter bar */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            sx={{ p: 2, pb: 1.75 }}
            alignItems={{ md: 'center' }}
            justifyContent="space-between"
          >
            <TextField
              size="small" placeholder="Search by name…" value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <Icon icon={searchFill} style={{ marginRight: 6, color: '#919EAB' }} /> }}
              sx={{ minWidth: 280 }}
            />
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {STATUS_FILTERS.map((s) => {
                const selected = statusFilter === s.key;
                return (
                  <Chip
                    key={s.key}
                    size="small"
                    label={s.label}
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    onClick={() => setStatusFilter(s.key)}
                    sx={{ fontWeight: selected ? 700 : 500, cursor: 'pointer' }}
                  />
                );
              })}
            </Stack>
          </Stack>
          <Divider />

          {!filtered ? (
            <Box sx={{ p: 2 }}><LinearProgress /></Box>
          ) : filtered.length === 0 && !search && statusFilter === 'all' ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">No campaigns yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Build your first multi-step sequence to start outreaching qualified companies.
              </Typography>
              <Button variant="contained" startIcon={<Icon icon={flashFill} />}
                      onClick={() => setChatOpen(true)}>
                Create your first campaign
              </Button>
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No campaigns match your search / filter.
              </Typography>
              <Button
                size="small" sx={{ mt: 1 }}
                onClick={() => { setSearch(''); setStatusFilter('all'); }}
              >
                Reset filters
              </Button>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 360px)' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{
                    '& th': {
                      bgcolor: 'background.neutral',
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      py: 1.25,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                  }}>
                    <TableCell sx={{ minWidth: 240 }}>Campaign</TableCell>
                    <TableCell sx={{ width: 120 }}>Status</TableCell>
                    <TableCell align="right" sx={{ width: 100 }}>Enrolled</TableCell>
                    <TableCell align="right" sx={{ width: 100 }}>Sent</TableCell>
                    <TableCell align="right" sx={{ width: 100 }}>Replied</TableCell>
                    <TableCell sx={{ width: 130 }}>Started</TableCell>
                    <TableCell align="right" sx={{ width: 240 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow
                      key={r.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '& td': { py: 1.25, borderBottom: '1px solid', borderColor: 'divider' },
                        '&:last-child td': { borderBottom: 'none' },
                      }}
                      onClick={() => navigate(`/outreach/campaigns/${r.id}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
                          {r.name}
                        </Typography>
                        {r.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              maxWidth: 400,
                            }}
                          >
                            {r.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusChip status={r.display_status || r.status} />
                        {(r.enrichment_progress?.in_flight ?? 0) > 0 && (
                          <Box sx={{ mt: 0.5, width: 112 }}>
                            <LinearProgress
                              variant="determinate"
                              value={r.enrichment_progress.percent}
                              sx={{ height: 4, borderRadius: 2 }}
                            />
                            <Typography variant="caption" color="text.secondary"
                                        sx={{ fontSize: '0.65rem', display: 'block', mt: 0.25 }}>
                              Validating {r.enrichment_progress.percent}%
                              {' '}({r.enrichment_progress.done}/{r.enrichment_progress.total})
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {r.counts.enrolled_count.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {r.counts.sent_count.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: r.counts.replied_count > 0 ? 700 : 400,
                        color: r.counts.replied_count > 0 ? 'success.dark' : 'inherit',
                      }}>
                        {r.counts.replied_count.toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        {fmt(r.started_at)}
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<Icon icon={pieChart2Fill} width={14} />}
                            sx={{ minWidth: 0, px: 1.25, '& .MuiButton-startIcon': { mr: 0.5 } }}
                            onClick={() => navigate(`/outreach/campaigns/${r.id}`)}
                          >
                            Overview
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<Icon icon={messageSquareFill} width={14} />}
                            sx={{ minWidth: 0, px: 1.25, '& .MuiButton-startIcon': { mr: 0.5 } }}
                            onClick={() => navigate(`/outreach/campaigns/${r.id}/conversations`)}
                          >
                            View
                          </Button>
                          <Tooltip title="More actions">
                            <IconButton size="small" onClick={(e) => onMenuOpen(e, r)}>
                              <Icon icon={moreVerticalFill} width={18} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        <Menu anchorEl={menu.anchor} open={Boolean(menu.anchor)} onClose={onMenuClose}>
          <MenuItem onClick={() => onAction('edit')}>Edit</MenuItem>
          {menu.row?.status === 'draft'  && <MenuItem onClick={() => onAction('activate')} sx={{ color: 'success.main', fontWeight: 700 }}>Activate</MenuItem>}
          {menu.row?.status === 'active' && <MenuItem onClick={() => onAction('pause')}>Pause</MenuItem>}
          {menu.row?.status === 'paused' && <MenuItem onClick={() => onAction('resume')}>Resume</MenuItem>}
          {menu.row?.status !== 'archived' && <MenuItem onClick={() => onAction('archive')}>Archive</MenuItem>}
        </Menu>

        <ChatCampaignWizard
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          onLaunched={() => load()}
        />
      </Container>
    </Page>
  );
};

export default CampaignList;
